import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

/**
 * Reputation Hub review widget. The embed pulls ~hundreds of KB plus Google
 * Fonts and causes layout shift, so we defer both the iframe and its script
 * until the first genuine user interaction. Space is reserved up-front to
 * avoid CLS when it activates.
 */
const ReviewWidget = () => {
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (activated) return;
    const events = ["pointerdown", "pointermove", "touchstart", "keydown", "wheel"];
    const onInteract = () => {
      setActivated(true);
      events.forEach((e) => window.removeEventListener(e, onInteract));
    };
    events.forEach((e) => window.addEventListener(e, onInteract, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onInteract));
  }, [activated]);

  useEffect(() => {
    if (!activated) return;
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, [activated]);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-1 mb-3"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
            ))}
          </motion.div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from real Germantown and Memphis-area customers.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
          style={{ minHeight: 480 }}
        >
          {activated ? (
            <iframe
              className="lc_reviews_widget"
              src={siteConfig.widgets.reviewIframeSrc}
              frameBorder={0}
              scrolling="no"
              style={{ minWidth: "100%", width: "100%", border: "none", display: "block", minHeight: 480 }}
              title="Customer reviews"
            />
          ) : (
            <div aria-hidden="true" style={{ minHeight: 480 }} />
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
